import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';
import { useUsersStore } from '../store/useUsersStore';

const BIDS_LIMIT = 50;
// Backend saveBidService writes to top-level collection "bids". Do not use subcollection unless backend does.
const PARENT_FIELD = process.env.REACT_APP_BIDS_PARENT_FIELD || 'parent_uid';
const USE_SUBCOLLECTION = process.env.REACT_APP_BIDS_USE_SUBCOLLECTION === 'true';
const isDev = process.env.NODE_ENV !== 'production';

/** Normalize bid doc so project_id and bidder_id are always present (backend may use projectId/bidderId/user_bid_id). */
function normalizeBid(docSnap) {
  const data = docSnap.data();
  const createdAt = data.created_at?.toMillis?.() ?? data.created_at ?? data.date ?? null;
  const projectId = data.project_id ?? data.projectId ?? '';
  const bidderId = data.bidder_id ?? data.bidderId ?? data.user_bid_id ?? data.user_id ?? '';
  return {
    id: docSnap.id,
    ...data,
    project_id: projectId,
    projectId,
    bidder_id: bidderId,
    bidderId,
    created_at: createdAt,
    date: createdAt ?? data.date,
  };
}

/**
 * useMyBids – Firestore real-time listener for the current user's bids (manual + auto-bid).
 *
 * How it works:
 * - Subscribes to Firestore collection "bids" with where('parent_uid', '==', user.uid), limit(50).
 * - When the backend writes a bid (saveBidService), the snapshot fires and state updates.
 * - hasBidOn(projectId) returns true if the selected sub-user has a bid on that project (from Firestore or optimistic pending).
 * - Projects passes hasBidOn and addBidProjectId to ProjectList → ProjectCard so cards show "You have already placed a bid" and stay in sync.
 *
 * Backend contract: saveBidService writes to top-level "bids" with parent_uid, bidder_id, project_id, date, created_at.
 * Firestore rules must allow read where resource.data.parent_uid == request.auth.uid.
 */
export function useMyBids() {
  const { user } = useFirebaseAuth();
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const getSelectedUser = useUsersStore((s) => s.getSelectedUser);

  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingProjectIds, setPendingProjectIds] = useState([]);

  const selected = getSelectedUser?.() ?? null;
  const bidderId = selected?.user_bid_id ?? selected?.bidder_id ?? selected?.bidderId ?? null;
  const bidderIdStr = bidderId != null ? String(bidderId) : null;

  const hasBidOn = useCallback(
    (projectId) => {
      const pid = projectId != null ? String(projectId) : '';
      if (!pid) return false;
      if (pendingProjectIds.includes(pid)) return true;
      if (!bidderIdStr) return false;
      return bids.some(
        (b) =>
          String(b.project_id ?? b.projectId ?? '') === pid &&
          String(b.bidder_id ?? b.bidderId ?? '') === bidderIdStr
      );
    },
    [bids, bidderIdStr, pendingProjectIds]
  );

  const addBidProjectId = useCallback((projectId) => {
    if (projectId == null) return;
    const pid = String(projectId);
    setPendingProjectIds((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
  }, []);

  const isMountedRef = useRef(true);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setBids([]);
      setLoading(false);
      if (isDev) console.warn('[useMyBids] No Firebase user uid – listener not attached. Backend must set parent_uid to this uid when writing bids.');
      return;
    }

    isMountedRef.current = true;
    setLoading(true);
    let q;
    if (USE_SUBCOLLECTION) {
      const bidsRef = collection(db, 'users', uid, 'bids');
      q = query(bidsRef, limit(BIDS_LIMIT));
      if (isDev) console.log('[useMyBids] Listening to subcollection users/' + uid + '/bids (ensure backend writes there).');
    } else {
      const bidsRef = collection(db, 'bids');
      q = query(bidsRef, where(PARENT_FIELD, '==', uid), limit(BIDS_LIMIT));
      if (isDev) console.log('[useMyBids] Listening to top-level collection "bids" with', PARENT_FIELD, '=', uid, '– ensure backend saveBidService writes parent_uid with this value.');
    }

    let unsubscribe;
    try {
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const nextBids = snapshot.docs.map(normalizeBid);
          nextBids.sort((a, b) => (b.date ?? b.created_at ?? 0) - (a.date ?? a.created_at ?? 0));
          if (isDev) {
            const n = snapshot.docs.length;
            console.log('[useMyBids] Snapshot received:', n, 'bids. New bids from backend (manual/auto) will trigger this and update project cards.');
            if (n > 0) {
              const first = nextBids[0];
              console.log('[useMyBids] Sample bid: parent_uid=', first.parent_uid, 'bidder_id=', first.bidder_id, 'project_id=', first.project_id, '– verify these match your selected sub-user and project.');
            }
          }
          if (isMountedRef.current) {
            setBids(nextBids);
            setLoading(false);
            const seenIds = new Set(nextBids.map((b) => String(b.project_id ?? b.projectId ?? '')).filter(Boolean));
            setPendingProjectIds((prev) => prev.filter((pid) => !seenIds.has(pid)));
          }
        },
        (error) => {
          console.warn('[useMyBids] Firestore listener error:', error?.message ?? error, '– Check Firestore rules allow read where', PARENT_FIELD, '== auth.uid');
          if (isMountedRef.current) {
            setBids([]);
            setLoading(false);
          }
        }
      );
    } catch (setupError) {
      console.warn('useMyBids Firestore query setup failed:', setupError?.message ?? setupError);
      if (isMountedRef.current) {
        setBids([]);
        setLoading(false);
      }
    }

    return () => {
      isMountedRef.current = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user?.uid]);

  return {
    bids,
    hasBidOn,
    loading,
    addBidProjectId,
  };
}
